<template>
  <PModal
    title="Mettre en copie"
    @open="firstLoad"
    @close="emit('close')"
    :dialog-attrs="{ class: 'lg:[&_div.overflow-y-auto]:overflow-y-hidden' }"
  >
    <template #activator="{ open }">
      <PTooltip class="mr-1" label="Ajouter un utilisateur en copie" :delay="1000" :offset="5" placement="right">
        <PButton
          size="icon"
          mode="tertiary-borderless"
          color="custom"
          class="grow-0 text-primary hover:bg-black/10 rounded-md"
          @click="open"
          aria-label="Ajouter une personne en copie"
          data-sentry-id="AddCCUserBtn"
        >
          <OhVueIcon :name="readonly ? simpleUserIcon : addUserIcon" scale="1.2" />
        </PButton>
      </PTooltip>
    </template>
    <template #title="{ title }">
      <div v-if="!readonly" class="flex items-center">
        <h1 class="text-h4 font-bold mb-2">
          {{ title }}
        </h1>
        <SaveStatus :loading="saving" />
      </div>
    </template>
    <template #default>
      <div v-if="!loading" class="flex flex-col lg:h-[50vh]">
        <div v-if="!readonly" class="flex items-center gap-1 lg:gap-4">
          <SelectUser
            ref="selectUserEl"
            with-c-c
            class="grow"
            v-model="selected"
            label="Nom du destinataire ou de l'entité"
            help="Saisir ou séléctionner le nom du destinataire ou de l’entité"
          />
          <PTooltip
            :delay="1000"
            :offset="5"
            label="Impossible d'ajouter cette entrée car elle est déjà présente"
            :disabled="canAdd(selected)"
          >
            <PButton
              mode="secondary"
              class="mt-2.5"
              :disabled="!canAdd(selected)"
              @click="add"
              data-sentry-id="CCUserModalAddUserBtn"
              >Ajouter</PButton
            >
          </PTooltip>
        </div>
        <p class="text-h6 font-bold">Ont accès au parapheur :</p>
        <PAlert
          v-if="simpleFeatureStore.uiStates.ccModal.alertOpened"
          :type="AlertType.INFO"
          class="mt-2"
          closable
          @close="simpleFeatureStore.uiStates.ccModal.alertOpened = false"
        >
          Vous pouvez uniquement supprimer les personnes ou entités mises en copie tant que vous avez la main sur le
          parapheur.
        </PAlert>
        <ul class="mt-1.5 lg:h-0 lg:grow overflow-y-auto">
          <li v-for="(item, index) in items" :key="index" class="py-1.5">
            <div class="flex justify-between items-center">
              <div class="flex flex-col text-grey-50 overflow-hidden">
                <span class="whitespace-nowrap overflow-hidden text-ellipsis">
                  {{ item.name }}
                </span>
                <span class="whitespace-nowrap overflow-hidden text-ellipsis">
                  {{ item.mail }}
                </span>
              </div>
              <div class="flex flex-col gap-1v items-end shrink-0">
                <div class="flex gap-1">
                  <p v-if="item.type.flux">
                    <span
                      class="bg-purple-glycine-950 text-purple-glycine-sun-319 text-xs font-bold rounded-md py-1v px-1"
                      >FLUX</span
                    >
                  </p>
                  <p v-if="item.type.cc">
                    <span
                      class="bg-purple-glycine-950 text-purple-glycine-sun-319 text-xs font-bold rounded-md py-1v px-1"
                      >EN COPIE</span
                    >
                  </p>
                </div>
                <button
                  v-if="item.type.cc && !item.frozen && item.author?.id === authStore.currentUser?.id"
                  data-sentry-id="RemoveCC"
                  class="text-sm text-blue-france-sun-113"
                  :aria-label="`supprimer la mise en copie de ${item.name}`"
                  @click="remove(item)"
                >
                  Supprimer la mise en copie
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div v-else class="h-16">
        <Loader />
      </div>
    </template>

    <template #actions="{ close }">
      <PButton class="ml-auto" @click="close" data-sentry-id="CCUserModalSaveBtn"> Fermer </PButton>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import { AlertType, PAlert, PAutocomplete, PButton, PModal, PTooltip, useAlertManager, useIcons } from "@pasta/box-v2";
import { RiUserAddLine, RiUserLine } from "oh-vue-icons/icons";
import { OhVueIcon } from "oh-vue-icons";
import { computed, ref } from "vue";
import { type AgentsDirectoryAutocompleteItem } from "../../../dto/agents-directory.dto";
import { type LetterFileDto } from "../../../dto/letter-file.dto";
import { sortBy, uniqBy } from "lodash";
import SelectUser from "../../../components/SelectUser.vue";
import Loader from "../../../components/Loader.vue";
import { useSimpleFeatureStore } from "../../../stores/simple-features.store";
import { useLetterFileCCUserStore } from "../../../stores/cc-user.store";
import SaveStatus from "../SaveStatus.vue";
import { useAuthStore, type UserDto } from "@pasta/front-auth-v2";

interface Props {
  letterFile: LetterFileDto;
  readonly: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: "close"): void }>();

const [addUserIcon, simpleUserIcon] = useIcons(RiUserAddLine, RiUserLine);
const ccUserStore = useLetterFileCCUserStore();
const simpleFeatureStore = useSimpleFeatureStore();
const alertManager = useAlertManager();
const authStore = useAuthStore();

const selected = ref<AgentsDirectoryAutocompleteItem[]>([]);
const loading = ref(false);
const saving = ref(false);

interface DisplayFluxOrCCItem {
  id: number;
  name: string;
  mail: string;
  frozen?: boolean;
  type: { cc: boolean; flux: boolean };
  deleted?: boolean;
  author?: UserDto;
}

const items = computed(() => {
  let ccArr: DisplayFluxOrCCItem[] = ccUserStore.list.data.map(item => {
    return {
      ...item,
      type: { cc: true, flux: false },
    };
  });
  const flux = [...(props.letterFile.upstream || []), ...(props.letterFile.downstream || [])];
  if (props.letterFile.current) {
    flux.push(props.letterFile.current);
  }
  for (const el of flux) {
    const found = ccArr.find(item => item.mail === el.mail);
    if (found) {
      found.type.flux = true;
    } else {
      ccArr = [...ccArr, { ...el, type: { cc: false, flux: true } }];
    }
  }

  return sortBy(ccArr, item => item.mail.toLowerCase());
});

async function refresh() {
  await ccUserStore.fetchList(props.letterFile.id);
}

async function firstLoad() {
  try {
    loading.value = true;
    await refresh();
  } finally {
    loading.value = false;
  }
}

function canAdd(items: AgentsDirectoryAutocompleteItem[]): boolean {
  return items.some(item => {
    return !ccUserStore.list.data.some(cc => cc.mail === item.mail);
  });
}

const selectUserEl = ref<{ mainElement: typeof PAutocomplete } | null>(null);
async function add() {
  if (!selected.value.length) return;
  if (!canAdd(selected.value)) return;

  saving.value = true;

  const toAdd = uniqBy(
    selected.value.filter(item => !ccUserStore.list.data.some(cc => cc.mail === item.mail)),
    "mail"
  );

  try {
    for (const item of toAdd) {
      await ccUserStore.create({
        mail: item.mail,
        letterFile: props.letterFile.id,
      });
    }
    await refresh();
  } catch {
    alertManager.createAlert({
      message: "Une erreur est survenue durant la sauvegarde, veuillez rafraichir la page",
      type: AlertType.ERROR,
    });
  } finally {
    saving.value = false;
    if (selectUserEl.value?.mainElement.inputValue) {
      selectUserEl.value.mainElement.inputValue = "";
    }
    selected.value = [];
  }
}

async function remove(item: DisplayFluxOrCCItem) {
  saving.value = true;
  try {
    await ccUserStore.delete(item.id, props.letterFile.id);
    await refresh();
  } catch {
    alertManager.createAlert({
      message: "Une erreur est survenue durant la sauvegarde, veuillez rafraichir la page",
      type: AlertType.ERROR,
    });
  } finally {
    saving.value = false;
  }
}
</script>
